import React, { useEffect, useState } from 'react'
import {
  Typography,
  Card,
  Timeline as AntTimeline,
  Select,
  Row,
  Col,
  Tag,
  Spin,
  Alert,
  Input,
  Button,
  Space,
  Pagination,
  Switch,
  DatePicker,
} from 'antd'
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  FileTextOutlined,
  UserOutlined,
  ClockCircleOutlined,
  PauseCircleOutlined,
  CoffeeOutlined,
} from '@ant-design/icons'
import { useAppDispatch, useAppSelector } from '../hooks/redux'
import { apiService } from '../services/apiService'

interface TimelineEvent {
  id: number
  user_email: string
  user_name: string
  event_type: string
  company_name: string
  lead_id: number
  metadata: any
  timestamp: string
}

interface BreakItem {
  id: string
  type: 'break'
  duration_minutes: number
  timestamp: string
  start_time: string
  end_time: string
}

type TimelineItem = TimelineEvent | BreakItem

interface TimelineResponse {
  count: number
  next: string | null
  previous: string | null
  results: TimelineEvent[]
}

interface UserInfo {
  id: number
  email: string
  name: string
  is_admin: boolean
}

function Timeline() {
  const { user } = useAppSelector((state) => state.auth)
  const [timeline, setTimeline] = useState<TimelineEvent[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [allTimelineEvents, setAllTimelineEvents] = useState<TimelineEvent[]>([])
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingMessage, setLoadingMessage] = useState('')
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0 })
  const [users, setUsers] = useState<UserInfo[]>([])
  const [selectedUser, setSelectedUser] = useState<string>('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [showBreaks, setShowBreaks] = useState(true)
  const [dateRange, setDateRange] = useState<[string | null, string | null]>([null, null])

  const pageSize = 20

  const fetchAllUsers = async () => {
    if (user?.is_admin) {
      try {
        const allUsers = await apiService.getAllUsers()
        console.log('🔍 Debug: Fetched all users:', allUsers)
        setUsers(allUsers)
      } catch (err: any) {
        console.error('Failed to fetch users:', err)
        // Fallback: extract unique users from current timeline results
        const uniqueUsers = Array.from(new Set(timeline.map(event => event.user_email)))
        const fallbackUsers = uniqueUsers.map(email => ({
          id: 0,
          email,
          name: email.split('@')[0],
          is_admin: false
        }))
        console.log('🔍 Debug: Using fallback users:', fallbackUsers)
        setUsers(fallbackUsers)
      }
    } else {
      // For non-admin users, only show themselves in the filter
      // since they can only see their own timeline events anyway
      if (user?.email) {
        setUsers([{
          id: 0, // Auth user doesn't have ID field
          email: user.email,
          name: user.name || user.email.split('@')[0],
          is_admin: false
        }])
        console.log('🔍 Debug: Non-admin user, showing only self:', user.email)
      }
    }
  }

  const loadMorePagesForUser = async (userEmail: string) => {
    if (!user?.is_admin || userEmail === 'all') return

    setLoadingMore(true)
    setLoadingMessage(`Searching for ${userEmail.split('@')[0]}'s events...`)
    setLoadingProgress({ current: 0, total: 10 })

    try {
      const maxPages = 10 // Limit to prevent infinite loading
      const allEvents: TimelineEvent[] = [...timeline]
      let userEventsFound = allEvents.filter(event => event.user_email === userEmail).length
      let pagesWithUserEvents = userEventsFound > 0 ? 1 : 0

      for (let page = 2; page <= maxPages; page++) {
        setLoadingMessage(`Loading page ${page} of timeline events...`)
        setLoadingProgress({ current: page - 1, total: maxPages })

        const response: TimelineResponse = await apiService.getTimeline(page)
        if (!response.results || response.results.length === 0) {
          setLoadingMessage(`No more events found. Checked ${page - 1} pages.`)
          break
        }

        allEvents.push(...response.results)

        // Check if this page has events from the target user
        const pageUserEvents = response.results.filter(event => event.user_email === userEmail)
        if (pageUserEvents.length > 0) {
          pagesWithUserEvents++
          userEventsFound += pageUserEvents.length
          setLoadingMessage(`Found ${pageUserEvents.length} events from ${userEmail.split('@')[0]} on page ${page}! (${userEventsFound} total)`)
          console.log(`🔍 Debug: Found ${userEmail} events on page ${page}`)
        } else {
          setLoadingMessage(`Page ${page} loaded (no events from ${userEmail.split('@')[0]})`)
        }

        // Brief pause to show progress
        await new Promise(resolve => setTimeout(resolve, 300))

        // Stop if we've found a reasonable number of events from this user
        if (userEventsFound >= 20) {
          setLoadingMessage(`Found enough events (${userEventsFound}) for ${userEmail.split('@')[0]}. Finishing up...`)
          break
        }
      }

      if (userEventsFound === 0) {
        setLoadingMessage(`No recent events found for ${userEmail.split('@')[0]} in the last ${maxPages} pages`)
      } else {
        setLoadingMessage(`✅ Loaded ${userEventsFound} events for ${userEmail.split('@')[0]} from ${pagesWithUserEvents} pages`)
      }

      setAllTimelineEvents(allEvents)
      console.log(`🔍 Debug: Loaded total ${allEvents.length} events across pages`)

      // Show final message briefly before clearing
      setTimeout(() => {
        setLoadingMessage('')
        setLoadingProgress({ current: 0, total: 0 })
      }, 2000)

    } catch (err: any) {
      setLoadingMessage(`❌ Error loading events: ${err.message}`)
      console.error('Failed to load more pages:', err)
      setTimeout(() => {
        setLoadingMessage('')
        setLoadingProgress({ current: 0, total: 0 })
      }, 3000)
    } finally {
      setLoadingMore(false)
    }
  }

  const fetchTimeline = async (page = 1) => {
    try {
      setLoading(true)
      const userEmailParam = selectedUser !== 'all' ? selectedUser : undefined
      const startDateParam = dateRange[0] || undefined
      const endDateParam = dateRange[1] || undefined
      const statusParam = statusFilter !== 'all' ? statusFilter : undefined
      const searchParam = searchTerm || undefined
      const response: TimelineResponse = await apiService.getTimeline(page, userEmailParam, startDateParam, endDateParam, statusParam, searchParam)
      console.log('🔍 Debug: Fetched timeline events:', response.results?.length, 'events')
      console.log('🔍 Debug: User emails in timeline:', response.results?.map(e => e.user_email))
      setTimeline(response.results)
      setTotalCount(response.count)
    } catch (err: any) {
      setError(err.message || 'Failed to fetch timeline')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTimeline(currentPage)
  }, [currentPage, selectedUser, dateRange, statusFilter, searchTerm])

  useEffect(() => {
    // Fetch all users when component mounts or when user changes
    fetchAllUsers()
  }, [user?.is_admin, timeline])

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'lead_assigned':
        return <UserOutlined style={{ color: '#1890ff' }} />
      case 'lead_completed':
        return <CheckCircleOutlined style={{ color: '#52c41a' }} />
      case 'lead_released':
        return <CloseCircleOutlined style={{ color: '#faad14' }} />
      default:
        return <FileTextOutlined style={{ color: '#666' }} />
    }
  }

  const getEventColor = (eventType: string) => {
    switch (eventType) {
      case 'lead_assigned':
        return 'blue'
      case 'lead_completed':
        return 'green'
      case 'lead_released':
        return 'orange'
      default:
        return 'default'
    }
  }

  const formatEventTitle = (event: TimelineEvent) => {
    const action = event.event_type.replace('lead_', '').replace('_', ' ')
    return `${action.charAt(0).toUpperCase() + action.slice(1)} - ${event.company_name}`
  }

  const formatEventDescription = (event: TimelineEvent) => {
    const { metadata } = event
    switch (event.event_type) {
      case 'lead_assigned':
        return `Lead assigned with score ${metadata?.lead_score || 'unknown'}`
      case 'lead_completed':
        const status = metadata?.status === 'successful' ? '✅ Successfully' : '❌ Unsuccessfully'
        const timeTaken = metadata?.time_taken_minutes ? ` in ${metadata.time_taken_minutes} minutes` : ''
        return `${status} completed${timeTaken}`
      case 'lead_released':
        return `Released from queue`
      case 'lead_abandoned':
        return `Lead lock expired`
      default:
        return 'Activity recorded'
    }
  }

  const insertBreaks = (events: TimelineEvent[]): TimelineItem[] => {
    // Insert breaks when viewing a single user (admin) or when viewing own timeline (non-admin)
    const shouldShowBreaks = showBreaks && events.length >= 2 && (
      // Admin user with specific user selected
      (user?.is_admin && selectedUser !== 'all') ||
      // Non-admin user viewing their own timeline (all events are theirs anyway)
      (!user?.is_admin)
    );
    
    if (!shouldShowBreaks) {
      return events
    }

    // Sort events by timestamp (oldest first) for chronological processing
    const sortedEvents = [...events].sort((a, b) => 
      new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
    )
    
    const eventsWithBreaks: TimelineItem[] = []
    
    for (let i = 0; i < sortedEvents.length; i++) {
      // Check if there's a next event and look for gaps
      if (i < sortedEvents.length - 1) {
        const currentEvent = sortedEvents[i]
        const nextEvent = sortedEvents[i + 1]
        
        // Look for gaps between lead completion/release and next assignment
        const isEndEvent = currentEvent.event_type === 'lead_completed' || 
                          currentEvent.event_type === 'lead_released'
        const isStartEvent = nextEvent.event_type === 'lead_assigned'
        
        if (isEndEvent && isStartEvent) {
          const currentTime = new Date(currentEvent.timestamp)
          const nextTime = new Date(nextEvent.timestamp)
          const gapMinutes = (nextTime.getTime() - currentTime.getTime()) / (1000 * 60)
          
          // Insert break if gap is more than 5 minutes
          if (gapMinutes > 5) {
            const breakItem: BreakItem = {
              id: `break-${currentEvent.id}-${nextEvent.id}`,
              type: 'break',
              duration_minutes: Math.round(gapMinutes),
              timestamp: new Date(currentTime.getTime() + 1000).toISOString(), // 1 second after completion for proper ordering
              start_time: currentEvent.timestamp,
              end_time: nextEvent.timestamp
            }
            
            // Add current event, then break, maintaining chronological order
            eventsWithBreaks.push(currentEvent)
            eventsWithBreaks.push(breakItem)
          } else {
            eventsWithBreaks.push(currentEvent)
          }
        } else {
          eventsWithBreaks.push(currentEvent)
        }
      } else {
        // Last event, just add it
        eventsWithBreaks.push(sortedEvents[i])
      }
    }
    
    // Sort final result back to reverse chronological order (newest first) for display
    eventsWithBreaks.sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )
    
    return eventsWithBreaks
  }

  // Use extended timeline data if we've loaded more pages for a specific user
  // Since server-side filtering handles user selection, always use current timeline page
  const eventsToFilter = timeline

  // All filtering is now handled server-side, so just use the events as-is
  const filteredEvents = eventsToFilter

  const timelineWithBreaks = insertBreaks(filteredEvents)

  // Server-side filtering now handles user selection, so all events should match the selected user

  if (loading && timeline.length === 0) {
    return <Spin size="large" style={{ display: 'flex', justifyContent: 'center', marginTop: 50 }} />
  }

  return (
    <div>
      <Typography.Title level={2} style={{ marginBottom: 24 }}>
        Activity Timeline
      </Typography.Title>

      {/* Filters */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]} align="top">
          {/* First Row - Main Filters */}
          <Col xs={24} sm={12} lg={6}>
            <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
              Search
            </label>
            <Input.Search
              placeholder="Search by company or user..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value)
                // Reset to page 1 when searching
                if (currentPage !== 1) {
                  setCurrentPage(1)
                }
              }}
              allowClear
            />
          </Col>

          {user?.is_admin && users.length > 0 && (
            <Col xs={24} sm={12} lg={6}>
              <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
                User
              </label>
              <Select
                style={{ width: '100%' }}
                placeholder="Filter by user"
                value={selectedUser}
                disabled={loadingMore}
                loading={loadingMore}
                onChange={async (value) => {
                  console.log('🔍 Debug: User filter changed to:', value)
                  setSelectedUser(value)

                  // Reset to page 1 when changing filters
                  if (currentPage !== 1) {
                    setCurrentPage(1)
                  }

                  // Load more pages if filtering by a specific user
                  if (value !== 'all' && user?.is_admin) {
                    await loadMorePagesForUser(value)
                  }
                }}
                showSearch
                filterOption={(input, option) => {
                  const children = option?.children as string | undefined
                  if (children) {
                    return children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  return false
                }}
              >
                <Select.Option value="all">All Users</Select.Option>
                {users.map(userItem => (
                  <Select.Option key={userItem.email} value={userItem.email}>
                    {userItem.name} ({userItem.email})
                  </Select.Option>
                ))}
              </Select>
              <div style={{
                fontSize: '11px',
                color: '#666',
                marginTop: '4px',
                minHeight: '16px',
                display: 'flex',
                alignItems: 'center'
              }}>
                {selectedUser !== 'all' && !loadingMore && (
                  <span>Filtering by: {selectedUser.split('@')[0]}</span>
                )}
                {loadingMore && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#1890ff',
                    fontWeight: '500'
                  }}>
                    <Spin size="small" />
                    <span>Loading...</span>
                  </div>
                )}
              </div>
            </Col>
          )}

          <Col xs={24} sm={12} lg={6}>
            <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
              Status
            </label>
            <Select
              style={{ width: '100%' }}
              placeholder="Filter by status"
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value)
                // Reset to page 1 when changing filters
                if (currentPage !== 1) {
                  setCurrentPage(1)
                }
              }}
            >
              <Select.Option value="all">All Events</Select.Option>
              <Select.Option value="successful">✅ Successful</Select.Option>
              <Select.Option value="unsuccessful">❌ Unsuccessful</Select.Option>
              <Select.Option value="completed">✅ Completed</Select.Option>
              <Select.Option value="locked">🔒 Assigned</Select.Option>
              <Select.Option value="released">🔄 Released</Select.Option>
            </Select>
          </Col>

          <Col xs={24} sm={12} lg={6}>
            <label style={{ fontSize: '12px', color: '#666', marginBottom: '4px', display: 'block' }}>
              Date Range
            </label>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              placeholder={['Start date', 'End date']}
              onChange={(dates, dateStrings) => {
                setDateRange(dateStrings as [string | null, string | null])
                // Reset to page 1 when changing date filter
                if (currentPage !== 1) {
                  setCurrentPage(1)
                }
              }}
              allowClear
            />
            <div style={{
              fontSize: '11px',
              color: '#666',
              marginTop: '4px',
              minHeight: '16px'
            }}>
              {(dateRange[0] || dateRange[1]) && (
                <span>
                  {dateRange[0] && dateRange[1] ?
                    `${dateRange[0]} to ${dateRange[1]}` :
                    dateRange[0] ? `From ${dateRange[0]}` : `Until ${dateRange[1]}`
                  }
                </span>
              )}
            </div>
          </Col>

          {/* Second Row - Actions */}
          <Col xs={24}>
            <Space wrap>
              <Button
                onClick={() => {
                  fetchTimeline(currentPage)
                  fetchAllUsers()
                }}
                loading={loading}
              >
                Refresh
              </Button>

              <Button
                onClick={() => {
                  setSelectedUser('all')
                  setSearchTerm('')
                  setStatusFilter('all')
                  setDateRange([null, null])
                  if (currentPage !== 1) {
                    setCurrentPage(1)
                  }
                }}
                type="default"
              >
                Clear Filters
              </Button>

              {((user?.is_admin && selectedUser !== 'all') || !user?.is_admin) && (
                <Space>
                  <Switch
                    checked={showBreaks}
                    onChange={setShowBreaks}
                    size="small"
                  />
                  <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                    Show breaks
                  </Typography.Text>
                </Space>
              )}

              <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                Showing {filteredEvents.length} of {totalCount} events
              </Typography.Text>
            </Space>
          </Col>
        </Row>
      </Card>

      {error && (
        <Alert
          message="Error loading timeline"
          description={error}
          type="error"
          closable
          style={{ marginBottom: 24 }}
        />
      )}

      {/* Timeline */}
      <Card style={{ position: 'relative' }}>
        {/* Prominent Loading Overlay */}
        {loadingMore && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '40px',
            borderRadius: '8px'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, #1890ff, #40a9ff)',
              borderRadius: '16px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(24, 144, 255, 0.3)',
              textAlign: 'center',
              maxWidth: '500px',
              width: '100%'
            }}>
              <Spin size="large" style={{ marginBottom: '24px' }}>
                <div style={{
                  width: '60px',
                  height: '60px',
                  borderRadius: '50%',
                  backgroundColor: 'rgba(255, 255, 255, 0.2)'
                }} />
              </Spin>

              <Typography.Title level={3} style={{ color: 'white', marginBottom: '16px' }}>
                🔍 Loading Timeline Events
              </Typography.Title>

              <Typography.Text style={{
                color: 'rgba(255, 255, 255, 0.9)',
                fontSize: '16px',
                fontWeight: '500',
                display: 'block',
                marginBottom: '16px'
              }}>
                {loadingMessage || 'Preparing to search...'}
              </Typography.Text>

              {loadingProgress.total > 0 && (
                <div style={{ marginTop: '20px' }}>
                  <div style={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    padding: '4px',
                    marginBottom: '8px'
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      height: '8px',
                      borderRadius: '8px',
                      width: `${(loadingProgress.current / loadingProgress.total) * 100}%`,
                      transition: 'width 0.3s ease-in-out'
                    }} />
                  </div>
                  <Typography.Text style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontSize: '14px'
                  }}>
                    Progress: {loadingProgress.current} / {loadingProgress.total} pages
                  </Typography.Text>
                </div>
              )}

              <Typography.Text style={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '12px',
                fontStyle: 'italic',
                marginTop: '12px',
                display: 'block'
              }}>
                💡 Searching through multiple pages to find all events for this user
              </Typography.Text>
            </div>
          </div>
        )}
        {showBreaks && ((user?.is_admin && selectedUser !== 'all') || !user?.is_admin) && (
          <Alert
            message="Break Detection Active"
            description="Showing breaks longer than 5 minutes between lead completion/release and next assignment."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        <AntTimeline>
          {timelineWithBreaks.map((item) => {
            // Check if this is a break item
            if ('type' in item && item.type === 'break') {
              const breakItem = item as BreakItem
              return (
                <AntTimeline.Item
                  key={breakItem.id}
                  dot={<CoffeeOutlined style={{ color: '#ff4d4f', fontSize: '18px' }} />}
                  color="red"
                >
                  <div style={{ 
                    paddingBottom: 16,
                    border: '2px solid #ff7875',
                    borderRadius: '8px',
                    padding: '16px',
                    backgroundColor: '#fff2f0',
                    boxShadow: '0 2px 8px rgba(255, 77, 79, 0.15)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                      <PauseCircleOutlined style={{ color: '#ff4d4f', fontSize: '20px' }} />
                      <Typography.Text strong style={{ 
                        color: '#ff4d4f', 
                        fontSize: '16px',
                        fontWeight: '600'
                      }}>
                        ⏸️ BREAK - {breakItem.duration_minutes} minutes
                      </Typography.Text>
                      <Tag color="red" style={{ 
                        fontSize: '12px', 
                        fontWeight: 'bold',
                        borderWidth: '2px'
                      }}>
                        🚫 IDLE
                      </Tag>
                    </div>
                    
                    <Typography.Text style={{ 
                      color: '#595959',
                      fontSize: '14px',
                      fontStyle: 'italic'
                    }}>
                      💤 No lead assigned for {breakItem.duration_minutes} minutes
                    </Typography.Text>
                    
                    <div style={{ 
                      marginTop: 8,
                      padding: '8px 12px',
                      backgroundColor: '#fff1f0',
                      borderRadius: '4px',
                      border: '1px solid #ffccc7'
                    }}>
                      <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                        <ClockCircleOutlined style={{ marginRight: 4, color: '#ff4d4f' }} />
                        📅 {new Date(breakItem.start_time).toLocaleString()} - {new Date(breakItem.end_time).toLocaleString()}
                      </Typography.Text>
                    </div>
                  </div>
                </AntTimeline.Item>
              )
            }

            // Regular timeline event
            const event = item as TimelineEvent
            return (
              <AntTimeline.Item
                key={event.id}
                dot={getEventIcon(event.event_type)}
              >
                <div style={{ paddingBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                    <Typography.Text strong>
                      {formatEventTitle(event)}
                    </Typography.Text>
                    <Tag color={getEventColor(event.event_type)}>
                      {event.event_type.replace('_', ' ')}
                    </Tag>
                    {user?.is_admin && (
                      <Tag color="purple">
                        {event.user_name}
                      </Tag>
                    )}
                    {/* Email count badge for completed leads */}
                    {event.event_type === 'lead_completed' && event.metadata?.emails_found != null && (
                      <Tag color={event.metadata.emails_found > 0 ? 'geekblue' : 'default'} style={{ fontWeight: 600 }}>
                        📧 {event.metadata.emails_found} email{event.metadata.emails_found !== 1 ? 's' : ''}
                      </Tag>
                    )}
                    {/* Phone count badge for completed leads */}
                    {event.event_type === 'lead_completed' && event.metadata?.phones_found > 0 && (
                      <Tag color="cyan" style={{ fontWeight: 600 }}>
                        📱 {event.metadata.phones_found} phone{event.metadata.phones_found !== 1 ? 's' : ''}
                      </Tag>
                    )}
                  </div>

                  <Typography.Text type="secondary">
                    {formatEventDescription(event)}
                  </Typography.Text>

                  {/* Rejection reason callout */}
                  {event.event_type === 'lead_completed' && event.metadata?.status === 'unsuccessful' && event.metadata?.unsuccessful_reason && (
                    <div style={{ marginTop: 6, padding: '6px 12px', backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 4 }}>
                      <Typography.Text style={{ color: '#cf1322', fontWeight: 600, fontSize: '13px' }}>
                        ❌ Reason: {event.metadata.unsuccessful_reason.replace(/_/g, ' ')}
                      </Typography.Text>
                      {event.metadata.unsuccessful_description && (
                        <Typography.Text style={{ color: '#595959', fontSize: '12px', display: 'block', marginTop: 2 }}>
                          {event.metadata.unsuccessful_description}
                        </Typography.Text>
                      )}
                    </div>
                  )}

                  {/* Release reason callout */}
                  {event.event_type === 'lead_released' && event.metadata?.release_reason && (
                    <div style={{ marginTop: 6, padding: '6px 12px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4 }}>
                      <Typography.Text style={{ color: '#d46b08', fontWeight: 600, fontSize: '13px' }}>
                        🔄 Reason: {event.metadata.release_reason.replace(/_/g, ' ')}
                      </Typography.Text>
                      {event.metadata.release_description && (
                        <Typography.Text style={{ color: '#595959', fontSize: '12px', display: 'block', marginTop: 2 }}>
                          {event.metadata.release_description}
                        </Typography.Text>
                      )}
                    </div>
                  )}

                  {/* Abandoned reason callout */}
                  {event.event_type === 'lead_abandoned' && (
                    <div style={{ marginTop: 6, padding: '6px 12px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
                      <Typography.Text style={{ color: '#389e0d', fontWeight: 600, fontSize: '13px' }}>
                        ⏰ {event.metadata?.reason === 'lock_expired' ? 'Lock expired — lead returned to queue' : `Abandoned: ${event.metadata?.reason || 'unknown'}`}
                      </Typography.Text>
                    </div>
                  )}

                  <div style={{ marginTop: 4 }}>
                    <Typography.Text type="secondary" style={{ fontSize: '12px' }}>
                      <ClockCircleOutlined style={{ marginRight: 4 }} />
                      {new Date(event.timestamp).toLocaleString()}
                      {event.metadata?.crm_link && (
                        <>
                          {' • '}
                          <a
                            href={event.metadata.crm_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: '#1890ff' }}
                          >
                            Lead #{event.metadata.crm_link.match(/\/leads\/(\d+)\//)?.[1]}
                          </a>
                        </>
                      )}
                    </Typography.Text>
                  </div>
                </div>
              </AntTimeline.Item>
            )
          })}
        </AntTimeline>

        {timelineWithBreaks.length === 0 && !loading && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Typography.Text type="secondary">
              No timeline events found
            </Typography.Text>
          </div>
        )}

        {/* Pagination - only show if we have more filtered events than page size and no active user filter */}
        {!selectedUser || selectedUser === 'all' ? (
          // Normal pagination for "All Users" view
          totalCount > pageSize && (
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Pagination
                current={currentPage}
                total={totalCount}
                pageSize={pageSize}
                onChange={setCurrentPage}
                showSizeChanger={false}
                showQuickJumper
                showTotal={(total, range) =>
                  `${range[0]}-${range[1]} of ${total} events`
                }
              />
            </div>
          )
        ) : (
          // For filtered views, show a simple count instead of pagination
          <div style={{ textAlign: 'center', marginTop: 24, color: '#666' }}>
            <Typography.Text type="secondary">
              Showing {filteredEvents.length} events for {selectedUser}
              {allTimelineEvents.length > timeline.length && (
                <span style={{ color: '#1890ff', marginLeft: 8 }}>
                  (loaded from multiple pages)
                </span>
              )}
            </Typography.Text>
          </div>
        )}
      </Card>
    </div>
  )
}

export default Timeline
